<template>
  <DpplHeaderFooterContent
    ref="container"
    class="outer-container px-3"
    :class="{
      'show-comments': showComments,
    }"
    @copy="trackCopyPaste('sideBySideCopyKeyboard')"
    @paste="trackCopyPaste('sideBySidePaste')"
  >
    <div class="w-100">
      <FirstDraftProgress />
      <div>
        <v-sheet
          id="title-company-location-container"
          :class="{
            'no-highlight': !showHeadingHighlights || sideBySideView,
            resized: true,
          }"
        >
          <div
            class="expand-container pa-6"
          >
            <div class="job-status-container">
              <JobStatus />
            </div>
            <div id="title-container">
              <TitleEditor
                v-if="!pageLoading"
                :value="jobTitle"
                :highlights="highlightsStore.titleHighlights"
                :is-read-only="!isEditable || isPublishedTemplate"
                :show-red-border="showRedBorderAroundTitle"
                :placeholder="`${documentTypeLabel} title`"
                @update:model-value="onEditorUpdate('title', $event)"
                @create="onEditorCreate('title', $event)"
              />
            </div>
            <div
              v-if="!isTemplate"
              id="company-location-container"
              :class="{ 'content-locked': whichCompanyLocationLocked === 'all' }"
            >
              <InlineEditor
                v-if="!pageLoading"
                :editor-id="'company'"
                :value="company"
                placeholder="Company"
                :is-read-only="!isEditable"
                @update:model-value="onEditorUpdate('company', $event)"
                @create="onEditorCreate('company', $event)"
              />
              <span
                id="company-location-separator"
                class="hyphen"
              >-</span>
              <div
                class="location-id-wrapper expand-container"
                :class="{
                  'content-locked': whichCompanyLocationLocked === 'partial',
                }"
              >
                <InlineEditor
                  v-if="!pageLoading"
                  :editor-id="'location'"
                  :value="location"
                  :highlights="highlightsStore.locationHighlights"
                  placeholder="Location"
                  :is-read-only="!isEditable || (Boolean(isLinked) && jobIntegrationTypeKey !== TYPE_DPPLANYWHERE)"
                  @update:model-value="onEditorUpdate('location', $event)"
                  @create="onEditorCreate('location', $event)"
                />

                <template v-if="!pageLoading && (!isLinked || jobIntegrationTypeKey === TYPE_DPPLANYWHERE)">
                  <span class="hyphen">-</span>
                  <InlineEditor
                    :editor-id="'flexibleId'"
                    :value="flexibleId"
                    :placeholder="isFlagship ? 'Metadata' : 'Job ID'"
                    :is-read-only="!isEditable || (Boolean(isLinked) && jobIntegrationTypeKey !== TYPE_DPPLANYWHERE)"
                    @update:model-value="onEditorUpdate('flexibleId', $event)"
                    @create="onEditorCreate('flexibleId', $event)"
                  />
                </template>

                <template v-if="atsRequisitionId">
                  <span class="hyphen">-</span>
                  <InlineEditor
                    :placeholder="isFlagship ? 'Metadata' : 'Job ID'"
                    :value="`Req ${atsRequisitionId}`"
                    :is-read-only="true"
                  />
                </template>
                <IconLock3
                  v-if="(!isEditable || !!isLinked) && whichCompanyLocationLocked === 'partial'"
                  class="lock-icon pa-0"
                />
              </div>
              <IconLock3
                v-if="(!isEditable || !!isLinked) && whichCompanyLocationLocked === 'all'"
                class="lock-icon"
              />
            </div>
          </div>
          <div
            v-if="reqReportUrl"
            class="view-req-report-container"
          >
            <ReqProgressButton
              v-if="!sideBySideView"
              class="d-flex align-end justify-end"
              @view-req-report="editorViewReqReportClicked(reqReportUrl)"
            />
          </div>
        </v-sheet>
        <div
          class="fields-container"
        >
          <TrField
            v-for="(field, fieldIndex) in fields"
            :id="field.id"
            :key="field.id"
            :ref="`field-${field.id}`"
            :type="field.type"
            :format-blacklist="field.formatBlacklist"
            :label="field.label"
            :index="fieldIndex"
            :sections="field.sections"
            :job="job"
            :last="fieldIndex === fields.length - 1"
            :update-and-save="updateAndSave"
            :editors-init-state="editorsInitState"
            :description="description"
            :set-description-action="setDescriptionAction"
            :editor-data-source="editorDataSource"
            :set-handle-cleanup-before-rescore-action="setHandleCleanupBeforeRescoreAction"
            :editor-initialized-action="editorInitializedAction"
            :project-integration-type="projectIntegrationType"
            :is-template="isTemplate"
            :is-flagship="isFlagship && isEditable"
            :is-editable="isEditable"
            :only="fields.length === 1"
            :focus="focus"
            :section-nudges="sectionNudges"
            :block-nudges="blockNudges"
          />
        </div>

        <div class="editor-footer mt-3 d-flex justify-space-between mb-8">
          <div class="flex-grow-1" />

          <div class="flex-grow-1 d-flex justify-end">
            <v-menu
              v-if="!sideBySideView"
              location="top-start"
              open-on-hover
              close-on-content-click
              transition="slide-y-transition"
              close-delay="200"
              open-delay="500"
            >
              <template #activator="{ props }">
                <button
                  v-bind="props"
                  type="button"
                  class="job-info-button mt-1 mr-3"
                  :disabled="jobInfoTags.length < 1"
                >
                  i
                </button>
              </template>

              <v-sheet
                width="auto"
                elevation="1"
                rounded="lg"
                class="pa-3"
              >
                {{ jobInfoTags.join(', ') }}
              </v-sheet>
            </v-menu>
          </div>
        </div>
      </div>
    </div>
    <FirstDraftModal />
  </DpplHeaderFooterContent>
</template>

<script>
/* eslint-disable vue/require-prop-types */
import { mapStores, mapState, mapActions } from 'pinia';
import { useAppLoading } from '@/stores/app/loading';
import { useCommenting } from '@/stores/commenting/commenting';
import { useHighlights } from '@/stores/highlights/highlights';
import { useTracking } from '@/stores/tracking/tracking';
import { useEditor } from '@/stores/editor/editor';
import { TYPE_DPPLANYWHERE } from '@/helpers/constants/integrationTypes';
import DpplHeaderFooterContent from '@ui/components/general/DpplHeaderFooterContent/DpplHeaderFooterContent.vue';
import InlineEditor from '@ui/components/editor/InlineEditor/InlineEditor.vue';
import IconLock3 from '@ui/assets/icons/Icon_Lock3.svg?component';
import TitleEditor from '@ui/components/editor/TitleEditor/TitleEditor.vue';
import FirstDraftModal from '@/components/firstDraft/FirstDraftModal.vue';
import FirstDraftProgress from '@/components/firstDraft/FirstDraftProgress.vue';
import ReqProgressButton from '@/components/editor/ReqProgressButton.vue';
import JobStatus from '@/components/editor/JobStatus.vue';
import TrField from './field/TrField.vue';

export default {
  name: 'EditorsContainer',
  components: {
    DpplHeaderFooterContent,
    FirstDraftModal,
    FirstDraftProgress,
    InlineEditor,
    TitleEditor,
    TrField,
    ReqProgressButton,
    JobStatus,
    IconLock3,
  },
  props: [
    'showRedBorderAroundTitle',
    'title',
    'company',
    'isTemplate',
    'isLinked',
    'location',
    'description',
    'isDataLoaded',
    'job',
    'documentTypeLabel',
    'jobId',
    'editorsInitState',
    'setDescriptionAction',
    'setLocationAction',
    'setCompanyAction',
    'setFlexibleIdAction',
    'setTitleAction',
    'editorDataSource',
    'setHandleCleanupBeforeRescoreAction',
    'isEditable',
    'updateAndSave',
    'editorInitializedAction',
    'projectIntegrationType',
    'jobIntegrationTypeKey',
    'tags',
    'view',
    'focus',
    'sectionNudges',
    'blockNudges',
    'flexibleId',
  ],
  data() {
    return {
      revisionId: this.$route.query?.sideRevision,
      TYPE_DPPLANYWHERE,
    };
  },
  computed: {
    ...mapStores(useHighlights),
    ...mapState(useAppLoading, ['pageLoading']),
    ...mapState(useEditor, [
      'fields',
      'loadingAnimation',
      'estimateLow',
      'estimateHigh',
      'atsRequisitionId',
      'reqReportUrl',
      'sideBySideView',
      'whichCompanyLocationLocked',
      'isPublishedTemplate',
      'isFlagship',
    ]),
    ...mapState(useEditor, { jobTitle: 'title' }),
    aiEnabled() {
      return this.authData?.aiEnabled;
    },
    showComments() {
      return useCommenting().showComments;
    },
    jobInfoTags() {
      const output = [];
      this.tags.forEach((tagGroup) => {
        tagGroup.tags.forEach((tag) => {
          output.push(tag.label);
        });
      });
      return output;
    },
    showHeadingHighlights() {
      return this.loadingAnimation.header || !this.loadingAnimation.showAnimation;
    },
  },

  methods: {
    ...mapActions(useTracking, ['editorViewReqReportClicked']),
    trackCopyPaste(eventName) {
      if (this.sideBySideView === 'sideRevision') {
        useTracking()[eventName]({
          id: this.jobId,
          revisionId: this.revisionId,
          revisionColumn: false,
          type: this.sideBySideView,
        });
      }
    },
    onEditorUpdate(type, { html }) {
      if (!this.editorsInitState[type]) {
        return;
      }

      if (type === 'title') {
        this.setTitleAction(html);
      } else if (type === 'company') {
        this.setCompanyAction(html);
      } else if (type === 'location') {
        this.setLocationAction(html);
      } else if (type === 'flexibleId') {
        this.setFlexibleIdAction(html);
      } else {
        return;
      }

      this.updateAndSave();
    },
    onEditorCreate(type) {
      this.editorInitializedAction(type);
    },
  },
};
</script>

<style scoped lang="less">
.outer-container {
  background-color: transparent;
  border: 0;
  flex-grow: 1;
  max-width: 1200px;
  user-select: none;
  padding-left: 16px;
  padding-right: 16px;
  .hyphen {
    line-height: 16px;
    padding: 0 8px;
    color: @text-darkest;
  }
  #company-location-container {
    margin-top: 8px;
  }
}
#title-container {
  line-height: 28px;
  min-height: 32px;
  word-break: break-word;
}
#company-location-container {
  display: flex;
  min-height: 20px;
  word-break: break-word;
  align-items: center;
}
.job-info-button {
  border-radius: 50%;
  width: 12px;
  height: 12px;
  text-align: center;
  line-height: 8px;
  border: 1px solid #969ca6;
  color: #969ca6;
  font-size: 10px;
  background: none;
}

#title-container.full-size {
  padding-top: 36px;
}
#title-company-location-container {
  display:flex;
  min-height: 100px;
  border-radius: 10px;

  &.stroke {
    border: 1px solid @stroke-default;
  }

  &.no-highlight {
    each(@severitySetSolid, .(@color, @severity-class) {
      .editor-module-hl-@{severity-class} {
        background-color: inherit;
      }
    })

    each(@severitySetUnderline, .(@color, @severity-class) {
      .editor-module-hl-@{severity-class} {
        border-bottom: 2px solid transparent;
      }
    })
  }

  &.resized {
    min-height: 76px;
  }
}

#company-location-container .editor-wrapper {
  display: flex;
}
.location-id-wrapper {
  display: flex;
}
.content-locked {
  background-color: fade(@bg-brand-default, 5%);
  padding: 2px 16px 2px 8px;
  border-radius: 5px;
}
.expand-container {
  min-width: 0;
  flex-grow: 1;
}
.lock-icon {
  width: 8px;
  height: 10px;
  fill: fade(@bg-brand-default, 40%);
  margin-left: auto;
  padding-left: 16px;
  align-self: center;
}
#company.company-unlocked {
  padding-top: 2px;
}
#location {
  max-width: 472px;
}
#location.location-width {
  max-width: 388px;
}
#location.locked-location {
  :deep(.ql-editor > p) {
    cursor: default;
  }
}
.fields-container {
  background-color: transparent;
  display: inline-block;
  min-width: 100%;
  min-height: 540px;
}

#req-id {
  font-size: 12px;
  font-weight: 300;
  line-height: 16px;
  user-select: text;
}

.view-req-report-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 0px 24px 24px 0px;
  margin-left: auto;
}

.job-status-container {
  display: flex;
  box-sizing: border-box;
  margin-bottom: 12px;
}
</style>
